import type React from 'react'
import {ActionList, VisuallyHidden} from '@primer/react'

import styles from './ReferenceList.module.css'

export interface ReferenceCardProps {
  /** Destination for the card; the whole card is a single link. */
  href: string
  /** Decorative leading visual shown before the title. Include any essential context in `title`. */
  leadingVisual?: React.ReactNode
  /** Primary text. */
  title: React.ReactNode
  /** Secondary text rendered beneath the title (e.g. a repo slug). */
  metadata?: React.ReactNode
  /** Trailing element, typically a status `Label`. */
  label?: React.ReactNode
  /** Anchor target. When `_blank`, an "(opens in a new tab)" hint is announced. */
  target?: React.HTMLAttributeAnchorTarget
  /** Anchor rel. Defaults to `noopener noreferrer` when `target="_blank"`. */
  rel?: string
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

/**
 * Purely presentational reference row: a single link built from `leadingVisual`,
 * `title`, `metadata`, and `label`. It holds no domain knowledge (no GitHub
 * parsing/normalization) — callers pass already-resolved UI props. Designed to be
 * rendered as a child of {@link ReferenceList}.
 */
export function ReferenceCard({href, leadingVisual, title, metadata, label, target, rel, onClick}: ReferenceCardProps) {
  const opensNewTab = target === '_blank'
  const resolvedRel = rel ?? (opensNewTab ? 'noopener noreferrer' : undefined)
  return (
    <ActionList.LinkItem
      href={href}
      target={target}
      rel={resolvedRel}
      onClick={onClick}
      size="large"
      className={metadata == null ? `${styles.card} ${styles.cardTitleOnly}` : styles.card}
    >
      {leadingVisual != null && <ActionList.LeadingVisual>{leadingVisual}</ActionList.LeadingVisual>}
      <span className={styles.title}>{title}</span>
      {opensNewTab && <VisuallyHidden>&nbsp;(opens in a new tab)</VisuallyHidden>}
      {metadata != null && (
        <ActionList.Description variant="block">
          <span className={styles.metadata}>{metadata}</span>
        </ActionList.Description>
      )}
      {label != null && <ActionList.TrailingVisual>{label}</ActionList.TrailingVisual>}
    </ActionList.LinkItem>
  )
}
